import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useReviewerQueue } from '../../../hooks/analytics/useReviewerReports.ts';
import { formatDate, RefreshMeta, StatusChip } from '../controller/shared.tsx';
import type { ReviewerQueueItem } from '../../../request/analytics/reviewerReports.ts';

/**
 * `reviewer_queue` — pending-review runs (workflow_status IN
 * ('complete','completed'), current), oldest first, per the vocabulary in
 * the feedback-round contract.
 */
export const ReviewerQueueTable: React.FC<{ rows?: ReviewerQueueItem[] }> = ({ rows: rowsOverride }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useReviewerQueue();
  const rows = useMemo(() => rowsOverride ?? data?.items ?? [], [rowsOverride, data]);

  const columns = useMemo<TableColumnDef<ReviewerQueueItem>[]>(
    () => [
      {
        id: 'control_title',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.control_title}</span>,
        widthPercent: 26,
      },
      {
        id: 'tester_name',
        columnLabel: 'Tester',
        header: () => 'Tester',
        cell: ({ row }) => row.original.tester_name ?? 'Unassigned',
        widthPercent: 16,
      },
      {
        id: 'workflow_status',
        columnLabel: 'Status',
        header: () => 'Status',
        cell: ({ row }) => <StatusChip value={row.original.workflow_status} />,
        widthPercent: 16,
      },
      {
        id: 'completed_at',
        columnLabel: 'Completed',
        header: () => 'Completed',
        cell: ({ row }) => formatDate(row.original.completed_at),
        widthPercent: 14,
      },
      {
        id: 'days_waiting',
        columnLabel: 'Waiting',
        header: () => 'Waiting',
        cell: ({ row }) => (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              row.original.days_waiting >= 5 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
            }`}
          >
            {row.original.days_waiting}d
          </span>
        ),
        widthPercent: 14,
      },
    ],
    []
  );

  return (
    <AppDataTable<ReviewerQueueItem>
      data={rows}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load the review queue.' : null}
      emptyMessage="Nothing pending review."
      searchable
      searchPlaceholder="Search the review queue..."
      paginated
      defaultPageSize={10}
      showPageSizeSelector
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default ReviewerQueueTable;
